import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  IconButton,
  LinearProgress,
  Paper,
  Collapse,
  Fade,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import Confetti from "react-confetti";
import { getHighestAvailableLifeExpectancy } from "../../../../../utils/assessmentDisplayUtils";
import BasicAssessmentStep from "./wizard-steps/BasicAssessmentStep";
import RecommendedAssessmentStep from "./wizard-steps/RecommendedAssessmentStep";
import AdvancedAssessmentStep from "./wizard-steps/AdvancedAssessmentStep";
import Modal from "../../../../shared/Modal";

import logger from '../../../../../utils/logger';

const STEPS = [
  {
    label: "Basic Assessment",
    description: "Initial health evaluation",
    accuracy: "Basic accuracy",
  },
  {
    label: "Recommended Assessment",
    description: "Family history and conditions",
    accuracy: "Improved accuracy (+2 years)",
  },
  {
    label: "Advanced Assessment",
    description: "Detailed health metrics",
    accuracy: "Maximum accuracy (+3 years)",
  },
  {
    label: "Summary",
    description: "Review your results",
    accuracy: "Final projection",
  },
];

const UnifiedAssessmentWizard = ({
  open,
  onClose,
  member,
  onUpdate,
  initialStep = 0,
}) => {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [formData, setFormData] = useState({
    basic: null,
    recommended: null,
    advanced: null,
  });
  const [savedData, setSavedData] = useState({
    basic: null,
    recommended: null,
    advanced: null,
  });
  const [isStepValid, setIsStepValid] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (open) {
      logger.info("Member data received:", JSON.stringify(member, null, 2));
      logger.info(
        "Basic Assessment data:",
        JSON.stringify(member?.basicAssessment, null, 2),
      );

      // Initialize with existing data if available
      const initialData = {
        basic: member?.basicAssessment
          ? {
              familyHistoryImpact: member.basicAssessment.family_history_impact,
              lifestyleImpact: member.basicAssessment.lifestyle_impact,
              healthStatusImpact: member.basicAssessment.health_status_impact,
              calculatedLifeExpectancy: parseFloat(
                member.basicAssessment.calculated_life_expectancy,
              ),
              healthScore: member.basicAssessment.calculated_health_score,
              isOverridden: Boolean(
                member.basicAssessment.manual_life_expectancy,
              ),
              userOverriddenLifeExpectancy:
                parseFloat(member.basicAssessment.manual_life_expectancy) || 0,
              assessment_date: member.basicAssessment.assessment_date,
              isValid: true,
            }
          : null,
        recommended: member?.recommendedAssessment || null,
        advanced: member?.advancedAssessment || null,
      };

      logger.info(
        "Transformed initial data:",
        JSON.stringify(initialData, null, 2),
      );

      // Set both form data and saved data to initial values
      setFormData(initialData);
      setSavedData(initialData);
      setActiveStep(initialStep);

      // Set completed steps based on existing data
      const completed = new Set();
      if (member?.basicAssessment?.assessment_date) completed.add(0);
      if (member?.recommendedAssessment?.assessment_date) completed.add(1);
      if (member?.advancedAssessment?.assessment_date) completed.add(2);
      setCompletedSteps(completed);

      initializedRef.current = true;
    }
  }, [open, member, initialStep]);

  const handleClose = () => {
    setFormData({
      basic: null,
      recommended: null,
      advanced: null,
    });
    setSavedData({
      basic: null,
      recommended: null,
      advanced: null,
    });
    setActiveStep(0);
    setShowConfetti(false);
    initializedRef.current = false;
    onClose();
  };

  const handleNext = () => {
    const stepToKey = {
      0: "basic",
      1: "recommended",
      2: "advanced",
    };

    // Save current step data before moving to next step
    const currentKey = stepToKey[activeStep];
    const currentStepData = currentKey ? formData[currentKey] : null;

    if (currentStepData?.isValid) {
      setCompletedSteps((prev) => new Set([...prev, activeStep]));
    }

    // If moving to summary step, show confetti
    if (activeStep === 2) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    // When going back, restore the saved data for that step
    const stepToKey = {
      0: "basic",
      1: "recommended",
      2: "advanced",
    };
    const currentKey = stepToKey[activeStep - 1];

    setFormData((prev) => ({
      ...prev,
      [currentKey]: savedData[currentKey],
    }));

    setActiveStep((prev) => prev - 1);
  };

  const handleSave = () => {
    // Save all assessment data up to the current step
    const dataToSave = {};

    if (formData.basic?.isValid) {
      dataToSave.basic = formData.basic;
    }
    if (formData.recommended?.isValid) {
      dataToSave.recommended = formData.recommended;
    }
    if (formData.advanced?.isValid) {
      dataToSave.advanced = formData.advanced;
    }

    // Update saved data with the new values
    setSavedData(formData);
    onUpdate(dataToSave);
    handleClose();
  };

  const handleStepUpdate = (data, step) => {
    logger.info(
      `Updating ${step} step with data:`,
      JSON.stringify(data, null, 2),
    );
    setFormData((prev) => ({
      ...prev,
      [step]: data,
    }));
    // Set step validity based on whether we have valid data
    setIsStepValid(Boolean(data?.isValid));
  };

  const getCurrentLifeExpectancy = () => {
    return getHighestAvailableLifeExpectancy(formData);
  };

  const renderSummaryStep = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <Fade in={true}>
        <Box>
          <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Assessment Complete!
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Your Comprehensive Life Expectancy Projection
          </Typography>
          <Typography variant="h2" color="primary" sx={{ my: 3 }}>
            {getCurrentLifeExpectancy()?.toFixed(1)} years
          </Typography>

          <Paper sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 4 }}>
            <Typography variant="subtitle1" gutterBottom>
              Assessment Summary
            </Typography>
            <Box sx={{ mt: 2 }}>
              {formData.basic && (
                <Typography variant="body2" gutterBottom>
                  • Base life expectancy adjusted for age and gender
                </Typography>
              )}
              {formData.recommended && (
                <Typography variant="body2" gutterBottom>
                  • Family history and health conditions analyzed
                </Typography>
              )}
              {formData.advanced && (
                <Typography variant="body2" gutterBottom>
                  • Detailed health metrics incorporated
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Box>
  );

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <BasicAssessmentStep
            data={formData.basic}
            member={member}
            onUpdate={(data) => handleStepUpdate(data, "basic")}
          />
        );
      case 1:
        return (
          <RecommendedAssessmentStep
            data={formData.recommended}
            previousData={formData.basic}
            member={member}
            onUpdate={(data) => handleStepUpdate(data, "recommended")}
          />
        );
      case 2:
        return (
          <AdvancedAssessmentStep
            data={formData.advanced}
            previousData={formData.recommended}
            member={member}
            onUpdate={(data) => handleStepUpdate(data, "advanced")}
          />
        );
      case 3:
        return renderSummaryStep();
      default:
        return null;
    }
  };

  return (
    <Modal 
      isOpen={open} 
      onClose={onClose}
      maxWidth="4xl"
      className="min-h-[80vh] max-h-[90vh] relative"
    >
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}

      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <Typography variant="h6">Health Assessment</Typography>
          <IconButton onClick={onClose} size="small" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <CloseIcon />
          </IconButton>
        </div>

        <Box sx={{ px: 3, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((step, index) => (
              <Step key={step.label} completed={completedSteps.has(index)}>
                <StepLabel>
                  <Typography variant="subtitle2">{step.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                  {index < 3 && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ display: "block" }}
                    >
                      {step.accuracy}
                    </Typography>
                  )}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <LinearProgress
            variant="determinate"
            value={(completedSteps.size / 3) * 100}
            sx={{ mt: 2, mb: 1, height: 6, borderRadius: 3 }}
          />
        </Box>

        <div className="mt-6 flex-grow overflow-y-auto">
          {renderStep()}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 mt-auto flex justify-between items-center">
          <div>
            <button
              onClick={onClose}
              className="mr-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors disabled:opacity-50"
              disabled={!isStepValid}
            >
              Save & Exit
            </button>
          </div>
          <div>
            {activeStep > 0 && (
              <button
                onClick={handleBack}
                className="mr-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-md transition-colors"
              >
                Back
              </button>
            )}
            {activeStep === STEPS.length - 1 ? (
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800"
              >
                Complete Assessment
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 disabled:opacity-50"
                disabled={!isStepValid}
              >
                {activeStep === 2 ? "View Summary" : "Next"}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UnifiedAssessmentWizard;
